import { Discount, Campaign } from '../../types/discounts';
import { AssetRequestObject, Product, ProductCatalog } from '../types';
import { SalesLocation } from '../../types/customers';

export enum ActionTypes {
    CreateCampaign = '[marketing/campaigns/create] CREATE_CAMPAIGN',
    CreateCampaignResponse = '[marketing/campaigns/create] CREATE_CAMPAIGN_RESPONSE',
    CreateDiscount = '[marketing/campaigns/create] CREATE_DISCOUNT',
    CreateDiscountResponse = '[marketing/campaigns/create] CREATE_DISCOUNT_RESPONSE',
    RemoveAsset = '[marketing/campaigns/create] REMOVE_ASSET',
    CreateAsset = '[marketing/campaigns/create] CREATE_ASSET',
    CreateAssetResponse = '[marketing/campaigns/create] CREATE_ASSET_RESPONSE',
    UploadAssetResponse = '[marketing/campaigns/create] UPLOAD_ASSET_RESPONSE',
    FetchLocations = '[marketing/campaigns/create] FETCH_LOCATIONS',
    FetchLocationsResponse = '[marketing/campaigns/create] FETCH_LOCATIONS_RESPONSE',
    FetchProductsCatalogs = '[marketing/campaigns/create] FETCH_PRODUCTS_CATALOGS',
    FetchProductsCatalogsResponse = '[marketing/campaigns/create] FETCH_PRODUCTS_CATALOGS_RESPONSE',
    FetchProducts = '[marketing/campaigns/create] FETCH_PRODUCTS',
    FetchProductsResponse = '[marketing/campaigns/create] FETCH_PRODUCTS_RESPONSE',
    FetchDiscount = '[marketing/campaigns/create] FETCH_DISCOUNT_PRODUCTS',
    FetchDiscountResponse = '[marketing/campaigns/create] FETCH_DISCOUNT_RESPONSE',
}

export interface CreateCampaign {
    type: ActionTypes.CreateCampaign;
    payload: {
        accountId: string;
        campaign: Campaign;
        discounts: Discount[];
        customers: string[];
    };
}

export interface CreateCampaignResponse {
    type: ActionTypes.CreateCampaignResponse;
    payload: Campaign;
}

export const createCampaign = (
    accountId: string,
    campaign: Campaign,
    discounts: Discount[],
    customers: string[]
): CreateCampaign => {
    return {
        type: ActionTypes.CreateCampaign,
        payload: {
            accountId,
            campaign,
            discounts,
            customers,
        },
    };
};

export interface CreateDiscount {
    type: ActionTypes.CreateDiscount;
    payload: {
        accountId: string;
        discount: Discount;
    };
}

export interface CreateDiscountResponse {
    type: ActionTypes.CreateDiscountResponse;
    payload: Discount;
}

export const createDiscount = (accountId: string, discount: Discount): CreateDiscount => {
    return {
        type: ActionTypes.CreateDiscount,
        payload: {
            accountId,
            discount,
        },
    };
};

export interface CreateAsset {
    type: ActionTypes.CreateAsset;
    payload: {
        accountId: string;
        asset: AssetRequestObject;
    };
}

export interface CreateAssetResponse {
    type: ActionTypes.CreateAssetResponse;
    payload: {
        accountId: string;
        uploadUrl: string;
        publicUrl: string;
        file: Blob;
        discountId: string;
    };
    meta: {
        cause: CreateAsset;
    };
}

export const createAsset = (accountId: string, asset: AssetRequestObject): CreateAsset => {
    return {
        type: ActionTypes.CreateAsset,
        payload: {
            accountId,
            asset,
        },
    };
};
export interface RemoveAsset {
    type: ActionTypes.RemoveAsset;
    payload: {
        discountId: string;
    };
}

export const removeAsset = (discountId: string): RemoveAsset => {
    return {
        type: ActionTypes.RemoveAsset,
        payload: {
            discountId,
        },
    };
};

export interface UploadAssetResponse {
    type: ActionTypes.UploadAssetResponse;
    meta: {
        cause: CreateAssetResponse;
    };
}

export interface FetchLocations {
    type: ActionTypes.FetchLocations;
    payload: {
        accountId: string;
        search?: string;
    };
}

export interface FetchLocationsResponse {
    type: ActionTypes.FetchLocationsResponse;
    payload: SalesLocation[];
    meta: {
        cause: FetchLocations;
    };
}

export const fetchLocations = (accountId: string, search?: string): FetchLocations => {
    return {
        type: ActionTypes.FetchLocations,
        payload: {
            accountId,
            search,
        },
    };
};

export interface FetchProductsCatalogs {
    type: ActionTypes.FetchProductsCatalogs;
    payload: {
        accountId: string;
    };
}

export interface FetchProductsCatalogsResponse {
    type: ActionTypes.FetchProductsCatalogsResponse;
    payload: ProductCatalog[];
    meta: {
        cause: FetchProductsCatalogs;
    };
}

export const fetchProductsCatalogs = (accountId: string): FetchProductsCatalogs => {
    return {
        type: ActionTypes.FetchProductsCatalogs,
        payload: {
            accountId,
        },
    };
};

export interface FetchProducts {
    type: ActionTypes.FetchProducts;
    payload: {
        accountId: string;
        catalogId: string;
    };
}

export interface FetchProductsResponse {
    type: ActionTypes.FetchProductsResponse;
    payload: Product[];
    meta: {
        cause: FetchProducts;
    };
}

export const fetchProducts = (accountId: string, catalogId: string): FetchProducts => {
    return {
        type: ActionTypes.FetchProducts,
        payload: {
            accountId,
            catalogId: catalogId,
        },
    };
};

export interface FetchDiscount {
    type: ActionTypes.FetchDiscount;
    payload: {
        accountId: string;
        discountId: string;
    };
}

export interface FetchDiscountResponse {
    type: ActionTypes.FetchDiscountResponse;
    payload: Discount;
}

export const fetchDiscount = (accountId: string, discountId: string): FetchDiscount => {
    return {
        type: ActionTypes.FetchDiscount,
        payload: {
            accountId,
            discountId,
        },
    };
};

export type Actions =
    | CreateCampaign
    | CreateCampaignResponse
    | CreateAsset
    | CreateAssetResponse
    | UploadAssetResponse
    | FetchLocations
    | FetchLocationsResponse
    | FetchProductsCatalogs
    | FetchProductsCatalogsResponse
    | FetchProducts
    | FetchProductsResponse
    | FetchDiscount
    | FetchDiscountResponse
    | RemoveAsset;
